import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input/Input";
import DataTable from "components/dataTable/DataTable";
import { ButtonNew } from "components/buttonNew";
import { TCustomerType } from "redux/reducers/models/facility";

import BookingFeeSearchEmptyState from "./BookingFeeSearchEmptyState";

type TBookingFeeCustomerTypesTableProps = {
  search: string;
  onSearchChange: (search: string) => void;
  filteredTypes: TCustomerType[];
  selectedIds: number[];
  isLoading?: boolean;
  onToggleType: (id: number) => void;
  onSelectAllToggle: (checked: boolean) => void;
  onSort?: (key: keyof TCustomerType, direction: "asc" | "desc") => void;
  onCreateCustomerType?: () => void;
  onClearAll?: () => void;
  searchInputId?: string;
  showSelectionMeta?: boolean;
  layout?: "modal" | "page";
};

export default function BookingFeeCustomerTypesTable(props: TBookingFeeCustomerTypesTableProps) {
  const { t } = useTranslation();
  const {
    search,
    onSearchChange,
    filteredTypes,
    selectedIds,
    isLoading = false,
    onToggleType,
    onSelectAllToggle,
    onSort,
    onCreateCustomerType,
    onClearAll,
    searchInputId = "booking-fee-customer-type-search",
    showSelectionMeta = false,
    layout = "modal",
  } = props;

  const showEmptyState = !isLoading && filteredTypes.length === 0 && search.length > 0;
  const hasTableResults = !isLoading && filteredTypes.length > 0;
  const allFilteredSelected =
    filteredTypes.length > 0 && filteredTypes.every(type => type?.id != null && selectedIds.includes(type.id));

  const tableScrollClassName =
    layout === "page"
      ? "booking-fees__customer-types-page-table-scroll"
      : "booking-fees__customer-types-modal-table-scroll";

  const tableClassName =
    layout === "page"
      ? "booking-fees__customer-types-page-table"
      : "booking-fees__customer-types-modal-table";

  return (
    <>
      <div className="booking-fees__customer-types-modal-toolbar">
        <div className="booking-fees__customer-types-modal-search">
          <label htmlFor={searchInputId} className="booking-fees__visually-hidden">
            {t("Search customer types")}
          </label>
          <Input
            id={searchInputId}
            value={search}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value)}
            type="search"
            placeholder={t("Search a customer type...")}
            labelHidden
          />
        </div>
        <ButtonNew
          size="small"
          type="secondary"
          disabled={!hasTableResults}
          onClick={() => onSelectAllToggle(!allFilteredSelected)}
        >
          {t("Select all")}
        </ButtonNew>
      </div>

      {showSelectionMeta ? (
        <div className="booking-fees__customer-types-page-meta">
          <span className="booking-fees__customer-types-page-meta-count">
            {selectedIds.length} {t("selected")}
          </span>
          {selectedIds.length > 0 && onClearAll ? (
            <>
              <span className="booking-fees__customer-types-page-meta-divider" aria-hidden="true" />
              <button
                type="button"
                className="booking-fees__customer-types-page-meta-clear"
                onClick={onClearAll}
              >
                <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                {t("Clear all")}
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {showEmptyState ? (
        <div
          className={
            layout === "page"
              ? "booking-fees__customer-types-page-empty"
              : "booking-fees__customer-types-modal-empty"
          }
        >
          <div className="booking-fees__customer-types-modal-empty-header">{t("Customer type")}</div>
          <BookingFeeSearchEmptyState
            title={t("No customer type found")}
            description={t("Your search '{{search}}' did not match any customer types.", { search })}
            descriptionSecondary={t("Please try again with a different search or create a new customer type.")}
            clearLabel={t("Clear search")}
            createLabel={t("New customer type")}
            clearDisabled={search.length === 0}
            onClearSearch={() => onSearchChange("")}
            onCreate={onCreateCustomerType}
          />
        </div>
      ) : (
        <div className={tableScrollClassName}>
          <DataTable
            className={tableClassName}
            loading={isLoading}
            columns={[{ id: "full_title", label: t("Customer type") }]}
            sort={onSort}
          >
            {filteredTypes.map(type => (
              <tr
                key={type?.id}
                className="clickable"
                onClick={() => type?.id != null && onToggleType(type.id)}
              >
                <td>
                  <div className="booking-fees__customer-types-modal-row">
                    <Checkbox
                      size="small"
                      checked={type?.id != null && selectedIds.includes(type.id)}
                      readOnly
                    />
                    <span>{type?.full_title ?? type?.title ?? ""}</span>
                  </div>
                </td>
              </tr>
            ))}
          </DataTable>
        </div>
      )}
    </>
  );
}
